import React from 'react';
import clsx from 'clsx';
import { ErrorMessageInput, Icon, Typography } from '@/components/common';
import { InputCheck, InputList, InputText } from '@/components/form';
import {
	Control,
	FieldErrors,
	FieldValues,
	useFieldArray,
	UseFormClearErrors,
	UseFormGetValues,
	UseFormRegister,
	UseFormSetValue,
} from 'react-hook-form';
import { MultipleChoiceQuestion, Option } from '@/interfaces';
import Icons from '@/const/icons';
import { ArrowDownIcon, ArrowUpIcon, Plus } from 'lucide-react';
import { errorFieldMessage } from '@/const';
import { MultipleAnswerErrorI } from '@/components/screens/lawyerapp/cases/case-administration/applicant-intake-phase/LandingPageCaseClaimForm';

interface Props {
	index: number;
	control: Control<FieldValues, any>;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	listCompanies: Option[];
	errors: FieldErrors<FieldValues>;
	getValues: UseFormGetValues<FieldValues>;
	clearErrors: UseFormClearErrors<FieldValues>;
	multipleChoiceQuestion: MultipleChoiceQuestion[] | undefined;
	multipleAnswersError: MultipleAnswerErrorI[];
	showMultipleAnswersError: boolean;
	isEditModeActived: boolean;
}

export const MultipleAnswersQuestion: React.FC<Props> = ({
	index,
	register,
	control,
	errors,
	setValue,
	getValues,
	clearErrors,
	listCompanies,
	multipleChoiceQuestion,
	multipleAnswersError,
	showMultipleAnswersError,
	isEditModeActived,
}) => {
	const isSaved = !!getValues(`questions[${index}].key`);

	const [maxLabelLength, setMaxLabelLength] = React.useState(0);

	const hasInitialized = React.useRef(false);
	const editAreaRef = React.useRef<HTMLDivElement>(null);
	const rules = {
		expected_answers: {
			required: { value: true, message: errorFieldMessage },
		},
	};

	const { fields, remove, append, swap } = useFieldArray({
		name: `questions[${index}].expected_answers`,
		control,
	});

	const getLabelText = (label: Option['label']): string => {
		if (typeof label === 'string') return label;

		return '';
	};

	React.useEffect(() => {
		const maxLength = listCompanies.reduce((acc, option) => {
			const labelText = getLabelText(option.label);
			return Math.max(acc, labelText.length);
		}, 0);

		setMaxLabelLength(maxLength * 10); //this 10 is a multiplier to adjust the width based on the average character width
	}, [listCompanies]);

	React.useEffect(() => {
		if (fields.length > 0) return;
		if (!hasInitialized.current && isSaved) {
			if (multipleChoiceQuestion && multipleChoiceQuestion.length > 0) {
				multipleChoiceQuestion.forEach((item) => {
					append({
						value: item.brand_id,
						detail: item.complement,
						isCorrect: item.is_correct,
					});
				});
			}
			hasInitialized.current = true;
		}

		if (!hasInitialized.current && !isSaved) {
			const initialOptions = Array.from({ length: 2 }, () => ({
				value: '',
				detail: '',
				isCorrect: false,
			}));
			append(initialOptions);
			hasInitialized.current = true;
		}
	}, [append, multipleChoiceQuestion, isSaved, fields]);

	const handleAddQuestion = () => {
		append({
			value: '',
			detail: '',
			isCorrect: false,
		});
	};
	const totalAnswers = fields.length;

	const handleMoveDown = (currentIndex: number) => {
		if (totalAnswers > currentIndex + 1 && totalAnswers > 1) {
			swap(currentIndex, currentIndex + 1);
		}
	};

	const handleMoveUp = (currentIndex: number) => {
		if (totalAnswers > currentIndex && totalAnswers > 1 && currentIndex > 0) {
			swap(currentIndex, currentIndex - 1);
		}
	};

	const handleRemove = (currentIndex: number) => {
		if (fields.length === 2) return;
		remove(currentIndex);
		clearErrors(`questions[${index}].expected_answers`);
	};

	const hasAtLeastOneChecked = getValues(
		`questions[${index}].expected_answers`
	)?.some((opt: any) => opt?.isCorrect);

	return (
		<div ref={editAreaRef} className="space-y-4" tabIndex={0}>
			<Typography type="regular-16">
				{isEditModeActived || !isSaved
					? 'Select all the correct answers'
					: 'Correct Answers'}
			</Typography>
			<div
				className={clsx({
					'space-y-6': !isEditModeActived,
					'space-y-4': isEditModeActived,
				})}
			>
				{fields.map((field, answerIndex) => {
					const value = getValues(
						`questions[${index}].expected_answers[${answerIndex}].value`
					);

					const detail = getValues(
						`questions[${index}].expected_answers[${answerIndex}].detail`
					);
					const answerHasError = multipleAnswersError
						.find((q) => q.index === index)
						?.errors.some(
							(e) => e.answerIndex === answerIndex && e.hasError === true
						);

					return (
						<div key={field.id}>
							{isEditModeActived || !value ? (
								<>
									<Typography type="regular-16" className="ml-7">
										{`Option ${answerIndex + 1}`}
									</Typography>
									<div className="flex items-start gap-2 w-full group">
										<InputCheck
											name={`questions[${index}].expected_answers[${answerIndex}].isCorrect`}
											register={register}
											className="size-6 bg-white rounded mt-3.5"
										/>
										<div className="w-full lg:w-[248px]">
											<InputList
												isSearch
												placeholder="Select Defendant Name"
												name={`questions[${index}].expected_answers[${answerIndex}].value`}
												myDefaultValue={value === null ? 'none' : value}
												register={register}
												options={listCompanies}
												classNameList="!overflow-y-auto !overflow-x-hidden"
												classNameInput="w-full group-hover:border-purple-150"
												rules={rules.expected_answers}
												setValueInput={setValue}
												error={
													(errors?.questions as any)?.[index]
														?.expected_answers?.[answerIndex]?.value
												}
												dynamicContainerWidth={
													maxLabelLength > 248 ? maxLabelLength : 248
												}
											/>
										</div>
										<InputText
											name={`questions[${index}].expected_answers[${answerIndex}].detail`}
											register={register}
											placeholder="Optional: Add details"
											classNameInput="lg:!w-[410px] group-hover:border-purple-150 !mt-1"
										/>

										<div className="space-x-7 opacity-0 group-hover:opacity-100 mt-5">
											<button
												type="button"
												onClick={() => handleRemove(answerIndex)}
												className={clsx({
													'opacity-30': fields.length === 2,
												})}
											>
												<Icon src={Icons.trash} className="!size-5" />
											</button>
											<button
												type="button"
												onClick={() => handleMoveDown(answerIndex)}
												className={clsx({
													'opacity-30':
														totalAnswers === 1 ||
														totalAnswers === answerIndex + 1,
												})}
											>
												<ArrowDownIcon className="text-[#707070] !size-5" />
											</button>
											<button
												type="button"
												onClick={() => handleMoveUp(answerIndex)}
												className={clsx({
													'opacity-30': totalAnswers === 1 || answerIndex === 0,
												})}
											>
												<ArrowUpIcon className="text-[#707070] !size-5" />
											</button>
										</div>
									</div>
								</>
							) : (
								<div className="flex items-center gap-6">
									<InputCheck
										name={`questions[${index}].expected_answers[${answerIndex}].isCorrect`}
										register={register}
										className="size-6 bg-white rounded"
										classNameIcon="w-5 h-5 text-green-60"
									/>
									<div className="flex items-center">
										{!value && detail ? 'None:' : ''}
										{value && (
											<Typography type="regular-16">
												{listCompanies.find((option) => option.name === value)
													?.label || value}
											</Typography>
										)}

										{detail && (
											<Typography type="regular-16">{`${
												value ? ':' : ''
											} ${detail}`}</Typography>
										)}
									</div>

									{/* show this error if both index match */}
									{answerHasError && showMultipleAnswersError && (
										<ErrorMessageInput message={errorFieldMessage} />
									)}
								</div>
							)}
						</div>
					);
				})}
			</div>
			{!hasAtLeastOneChecked && showMultipleAnswersError && (
				<ErrorMessageInput message={'Mark at least one option as correct'} />
			)}
			{isEditModeActived && (
				<button
					type="button"
					className="flex items-center gap-2"
					onClick={handleAddQuestion}
				>
					<Plus color="#8039DF" />
					<Typography type="semibold-16" className="text-purple-150">
						Add Options
					</Typography>
				</button>
			)}
		</div>
	);
};
